import type {
  ComplianceClientRow,
  MatchDisposition,
  MatchedIndividual,
  ScreeningDecision,
} from "@repo/shared/types";
import { dispositionsRejectDecision, matchKey } from "@repo/shared/types";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { MatchAdjudication } from "./match-adjudication";
import { T } from "./review-dialog.i18n";

type ReviewDialogProps = {
  client: ComplianceClientRow | null;
  onClose: () => void;
  onSubmit: (
    decision: ScreeningDecision,
    note: string,
    matchDispositions: Record<string, MatchDisposition>,
  ) => Promise<void>;
  /** Matched individuals from the newest successful screening, to adjudicate. */
  individuals: MatchedIndividual[];
  isPending: boolean;
  error: string | null;
};

/**
 * QL-21 — record a compliance verdict on a client's current findings.
 *
 * Shows the findings being judged, because the verdict is bound to them: it
 * lapses by itself once they change, and the reviewer should see what they are
 * signing off. A previous verdict is shown even when stale, so the reviewer
 * knows what was decided before and why it no longer counts.
 */
export function ReviewDialog({
  client,
  onClose,
  onSubmit,
  individuals,
  isPending,
  error,
}: ReviewDialogProps) {
  const t = useT(T);
  const { formatDate } = useRegionalFormat();
  const [decision, setDecision] = useState<ScreeningDecision>("accepted");
  const [note, setNote] = useState("");
  const [dispositions, setDispositions] = useState<Record<string, MatchDisposition>>({});

  // Reset per client so a verdict typed for one never carries to the next.
  useEffect(() => {
    setDecision(client?.review?.decision ?? "accepted");
    setNote("");
    // Prior per-match verdicts are only meaningful while they were made against
    // the CURRENT findings — a stale review describes matches that may no
    // longer be on the table, and the server rejects keys it does not
    // recognise. Reopening a stale review therefore starts clean.
    setDispositions(
      client?.review && !client.reviewStale ? (client.review.matchDispositions ?? {}) : {},
    );
  }, [client]);

  const matchKeys = useMemo(() => individuals.map(matchKey), [individuals]);
  const rejection = dispositionsRejectDecision(decision, matchKeys, dispositions);
  const blockedReason =
    rejection === null
      ? null
      : rejection.reason === "no_matches"
        ? t.noMatchesToRuleOut
        : t.matchesLeft(rejection.count);

  if (!client) return null;

  const findings = client.findings;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>{t.description(client.name)}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <div className="space-y-2">
            <Label>{t.findings}</Label>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-2">
                {t.pep}
                {/* Tri-state: null means nobody was asked. The reviewer has to
                    see that, because accepting an unassessed PEP question is a
                    different decision from accepting a clean one. */}
                {findings?.isPep === null || findings?.isPep === undefined ? (
                  <Badge variant="warning">{t.notAssessed}</Badge>
                ) : (
                  <Badge variant={findings.isPep ? "warning" : "success"}>
                    {findings.isPep ? t.yes : t.no}
                  </Badge>
                )}
              </span>
              <span className="flex items-center gap-2">
                {t.sanctioned}
                <Badge variant={findings?.isSanctioned ? "destructive" : "success"}>
                  {findings?.isSanctioned ? t.yes : t.no}
                </Badge>
              </span>
            </div>
            {/* Name the person the PEP finding is about. For a Company the
                verdict is being recorded against the REPRESENTATIVE's listing,
                and a reviewer signing that off has to know whose it is. */}
            {/* Only when a representative was ACTUALLY screened. `kind: "none"`
                means nobody was, and naming a representative there contradicts
                the "not assessed" finding directly above.
                Found by CodeRabbit CLI. */}
            {client.subject?.kind === "representative" && (
              <p className="text-xs text-muted-foreground">
                {t.subjectLine(client.subject.name ?? t.notAssessed)}
              </p>
            )}
          </div>

          {client.review && (
            <p className="text-sm text-muted-foreground">
              {/* The decision goes through the same labels as the radio
                  options — interpolating the raw value put an English
                  "accepted" into the Norwegian sentence. */}
              {(client.reviewStale ? t.previousStale : t.previous)({
                decision: (client.review.decision === "accepted"
                  ? t.accepted
                  : client.review.decision === "blocked"
                    ? t.blocked
                    : t.notAMatch
                ).toLowerCase(),
                by: client.review.decidedByName || "—",
                at: formatDate(client.review.decidedAt),
              })}
              {client.review.note ? ` — “${client.review.note}”` : ""}
            </p>
          )}

          <MatchAdjudication
            individuals={individuals}
            dispositions={dispositions}
            onChange={setDispositions}
            disabled={isPending}
          />

          <div className="space-y-3">
            <Label>{t.decision}</Label>
            <RadioGroup
              value={decision}
              onValueChange={(v) => setDecision(v as ScreeningDecision)}
              disabled={isPending}
            >
              <OptionRow>
                <RadioGroupItem value="accepted" id="decision-accepted" />
                <Label htmlFor="decision-accepted" className="font-normal cursor-pointer">
                  {t.accepted}
                  <span className="block text-xs text-muted-foreground">{t.acceptedHint}</span>
                </Label>
              </OptionRow>
              <OptionRow>
                <RadioGroupItem value="blocked" id="decision-blocked" />
                <Label htmlFor="decision-blocked" className="font-normal cursor-pointer">
                  {t.blocked}
                  <span className="block text-xs text-muted-foreground">{t.blockedHint}</span>
                </Label>
              </OptionRow>
              {/* QL-b — only offered when there is something to rule out. With
                  no matched individuals (a sanctioned ENTITY, which carries an
                  org number and is not ambiguous) "not a match" has no
                  meaning, and the server refuses it. */}
              {individuals.length > 0 && (
                <OptionRow>
                  <RadioGroupItem value="not_a_match" id="decision-not-a-match" />
                  <Label htmlFor="decision-not-a-match" className="font-normal cursor-pointer">
                    {t.notAMatch}
                    <span className="block text-xs text-muted-foreground">{t.notAMatchHint}</span>
                  </Label>
                </OptionRow>
              )}
            </RadioGroup>
            {/* The same rule the service enforces, surfaced before the round
                trip: "not a match" is only honest once every match is
                dismissed. */}
            {blockedReason && <p className="text-xs text-warning">{blockedReason}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-note">
              {t.note} <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="review-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t.notePlaceholder}
              rows={3}
              disabled={isPending}
            />
            <p className="text-xs text-muted-foreground">{t.noteRequired}</p>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t.cancel}
          </Button>
          <Button
            onClick={() => onSubmit(decision, note, dispositions)}
            disabled={!note.trim() || Boolean(blockedReason)}
            loading={isPending}
          >
            {t.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
